// API key is now server-side only — calls go through /api/claude

// ── Current user id for server-side cost attribution ─────────────────────────
// AuthContext pushes the signed-in user's id here on login/logout so every
// Anthropic call we make gets stamped onto claude_usage rows for the admin
// spend dashboard. Null when no one is signed in.
let _currentUserId = null;
export function setCurrentUserId(id) { _currentUserId = id || null; }
export function getCurrentUserId()    { return _currentUserId; }

// ── Niche trending data ───────────────────────────────────────────────────────
function trends(niche) {
  const n = (niche || '').toLowerCase()
  const map = {
    'mental health': `YOUTUBE OUTLIERS — Mental health :
• "I Quit Therapy. Here's What Nobody Tells You." — 3.2M views (MedCircle)
• "What Your Therapist Notices But Won't Tell You" — 2.8M views (Psych2Go)
• "The Real Reason You Can't Stop Overthinking" — 2.1M views (Dr. Julie Smith)
• "I Told My Therapist Everything. This Happened." — 1.9M views (Kati Morton)
TOP TITLE PATTERNS: Direct address ("Your therapist...") · Confession format ("I did X") · Reveal format ("What nobody tells you") · Contrarian ("I quit X")
HOOK PATTERNS: Personal shame-naming → 3x completion · "Nobody tells you" surging · Contrarian therapy takes avg 2.3M`,

    'self-help': `YOUTUBE OUTLIERS — Self-help :
• "I Stopped Trying to Be Productive. My Life Changed." — 4.1M views (Matt D'Avella)
• "The Morning Routine That Actually Works (Not What You Think)" — 2.6M views
• "Why You're Stuck (And It's Not What You Think)" — 2.2M views (Struthless)
• "I Spent 30 Days Doing Nothing. Here's What I Learned." — 1.8M views
TOP TITLE PATTERNS: "I did X for Y days" · Contrast format ("Not what you think") · Stuck/unstuck framing · Identity challenge
HOOK PATTERNS: Identity-shift hooks dominate · "Unpopular opinion" 68% above avg`,

    'business': `YOUTUBE OUTLIERS — Business :
• "I Lost $400,000 Starting a Business (Here's What I Learned)" — 3.8M views (Alex Hormozi)
• "The Business Model Nobody Is Talking About in 2025" — 2.4M views
• "I Quit My Job With $0 Saved. This Is What Happened." — 2.1M views
• "Why 95% of Businesses Fail in Year One" — 1.7M views
TOP TITLE PATTERNS: Specific dollar figures · "Nobody is talking about" · Failure-first · Year-specific ("in 2025")
HOOK PATTERNS: Failure-first hooks 1.2M+ avg · Specific numbers boost CTR 38%`,

    'true crime': `YOUTUBE OUTLIERS — True crime :
• "The Case That Stumped Everyone. Until Now." — 3.1M views
• "She Told Police Exactly What Happened. Nobody Believed Her." — 2.7M views
• "The Killer Left This Behind. Police Ignored It for 20 Years." — 2.2M views
TOP TITLE PATTERNS: Passive voice builds suspense · "Until now" creates urgency · Disbelief framing ("nobody believed")
HOOK PATTERNS: In-medias-res openers · Hyperlocal specificity · Listener-implicating questions`,

    'entrepreneurship': `YOUTUBE OUTLIERS — Entrepreneurship :
• "I Failed 6 Times Before This. Here's What Changed." — 3.4M views
• "The $0 Business Anyone Can Start in 2025" — 2.9M views (Graham Stephan)
• "What Nobody Tells You Before You Quit Your Job" — 2.3M views
TOP TITLE PATTERNS: Failure + lesson · $0 or free angle · "Before you X" warning format
HOOK PATTERNS: Honest failure + lesson is #1 performing style · Solopreneur content outperforming corporate 60%`,

    'health & fitness': `YOUTUBE OUTLIERS — Health & fitness :
• "I Worked Out Every Day for a Year. Here's the Truth." — 4.2M views
• "The One Exercise Your Doctor Isn't Telling You About" — 3.1M views (Thomas DeLauer)
• "I Ate X Every Day for 30 Days. This Happened to My Body." — 2.6M views
TOP TITLE PATTERNS: "I did X for Y days/year" · "One thing" framing · Doctor/expert authority challenge
HOOK PATTERNS: "I tried X" still converting at peak rates · Contrarian science hooks 2.8M+ avg`,

    'default': `YOUTUBE OUTLIERS — Cross-niche patterns :
• Contrarian hooks consistently outperform inspirational across every niche
• "I did X for Y days" remains highest-converting format (2M+ avg views)
• "What nobody tells you about X" surging across categories
• Failure-first + lesson format outperforming success stories 2:1
• Specific numbers in titles boost CTR 35-40% vs vague language
• Direct address ("Your [role]...") creates strong pattern interrupt`
  }
  return map[Object.keys(map).find(k => n.includes(k)) || 'default']
}

function socialBlock(profile) {
  if (!profile) return ''
  const links = []
  if (profile.youtube_url)   links.push(`▶️ Subscribe on YouTube: ${profile.youtube_url}`)
  if (profile.spotify_url)   links.push(`🎧 Listen on Spotify: ${profile.spotify_url}`)
  if (profile.instagram_url) links.push(`📸 Follow on Instagram: ${profile.instagram_url}`)
  if (profile.tiktok_url)    links.push(`🎵 Follow on TikTok: ${profile.tiktok_url}`)
  if (profile.website_url)   links.push(`🌐 Visit: ${profile.website_url}`)
  return links.length ? `\n\n${links.join('\n')}` : ''
}

const VIRALITY_RUBRIC = `VIRALITY SCORING (1.0–10.0 — be honest, a real 6.3 beats a fake 9):
Pattern match (0–2.5): Matches top-performing format in this niche right now?
Keyword gravity (0–2.5): High-search words, emotional triggers, specificity?
Scroll-stop (0–2.5): Creates open loop, challenges belief, mirrors listener?
Specificity (0–2.5): Real details, numbers, named moments?
Always cite a specific viral comparison (real platform + approximate views + timeframe)`

// Shared hook-writing psychology used by every generator that produces
// hooks (long-form episode hooks, regenerate-section hook beat, overlay
// candidates). Pattern matches the short-form HOOK_FRAMEWORK so a
// creator's voice stays consistent across formats: the hook is FOMO +
// EMOTIONAL INTIMACY, layered. The TURN inside a tiny story arc lands
// only because the listener's guard came down on the opening line.
const HOOK_PSYCHOLOGY = `HOOK PSYCHOLOGY — every hook on this episode (the cold-open line, the YouTube title, the spoken first sentence) must obey two simultaneous rules:

1. FOMO. The listener feels they're catching the middle of something — that other people already know this and they don't. Mid-thought open. Reference without setup. Specificity over abstraction.

2. EMOTIONAL INTIMACY. The hook lands as if it is being whispered to ONE listener whose inner life the speaker has been quietly observing. The listener should think "wait, how did they know I've been feeling that?". You are NOT addressing an audience — you are naming a feeling they have carried privately for a long time. The unspoken thing they couldn't put into words. You put it into words for them, gently, before they can put their guard back up.

3. AUDIENCE-FOCUSED, NOT SPEAKER-FOCUSED. The hook is ABOUT THE LISTENER, not about the host. The listener is the protagonist of the line, not the audience to a memoir. Two failure modes to avoid:
   • SELF-FOCUSED HOOKS — die. ("This is what I learned about XYZ" / "How I built my podcast" / "What therapy taught me.")
   • AUDIENCE-FOCUSED HOOKS — work. ("Nobody told you this, but this is the only way you actually do XYZ" / "Here's why what you've been doing isn't working — and what does" / "If you've felt this and didn't have words for it, listen.")
   First-person CAN show up later in the body as a confession or anchor, but the HOOK itself must be a line about the listener's life, the listener's stuck-place, the listener's unspoken feeling. Speak TO them, not AT them, not ABOUT yourself.

How to write it:
- Talk to ONE person, not "you guys", not "everyone".
- Use language that sounds borrowed from the listener's own internal monologue.
- Name the small private feeling — the 11 p.m. doom-scroll, the parking-lot cry, the version of themselves they've been quietly grieving.
- Sound like a kind, observant friend who just saw through them — never a brand, never a teacher.
- The instant the line sounds like marketing, the wall goes back up. Lower-case fragments, contractions, real human cadence.
- Self-check: does this hook open with what the LISTENER is going through, or what the SPEAKER experienced? If speaker-first, rewrite.

What this unlocks: the listener's defenses don't engage. By the time the episode's first real claim hits, the wall is already down — and the rest of the conversation can plant something real. Hooks that combine FOMO + INTIMACY measurably out-retain hooks that hit only one. ALWAYS LAYER BOTH.`

// Tone vocabulary lifted from the Advanced builder's BuilderDialIns —
// 10 normalized tones the creator can pick. Mirror is intentional so
// long-form, short-form, and regen all share one voice palette.
const TONE_HANDLING = `TONE HANDLING — the creator's brief carries a tone label. Treat it as a directive on rhythm, vocabulary, and emotional palette across every text field you generate (hooks, titles, segment notes, descriptions, captions). Hook psychology + story arc still apply — tone shapes HOW the words land, not WHETHER the rules apply.

• conversational — relaxed, friend-on-the-couch cadence. Contractions. No performance.
• storytelling — lean fully into the story arc; cinematic specificity over claims; let scene-setting breathe.
• hard-hitting — short fragments, blunt verbs, sentences that punch. Direct address. No hedging.
• inspiring — rising rhythm; the close should feel like a small permission slip; warm, not preachy.
• calm — slower cadence, longer breaths, warm-quiet voice; like a 2 a.m. text from someone who knows.
• high-energy — kinetic verbs, momentum that pulls the listener forward; still emotionally precise.
• witty — dry, observational humor that lands the truth sideways; wit must reveal, never deflect.
• documentary — narrator voice in places, named details with date/place precision; reads like a 60-second NPR opener.
• reflective — first-person confession; quiet, internal; the "I noticed something" voice.
• urgent — present tense, short sentences; stakes-forward without ever sounding like a sales pitch.

If no tone is provided, default to "conversational" with a strong INTIMACY layer.`

// ── Episode Builder ───────────────────────────────────────────────────────────
export async function generateEpisode(d, profile) {

  const isInterview = ['guest','interview','co-host','cohost'].some(w => d.format?.toLowerCase().includes(w))
  const social = socialBlock(profile)
  const show = d.show_name || profile?.show_name || d.showName || 'the show'

  const system = `You are Epio — the world's best podcast producer, YouTube growth strategist, and interview coach.

Generate a COMPLETE, SPECIFIC episode production package. Generic output is failure. Every line must be specific to THIS topic, THIS audience, THIS creator.

CREATOR BRIEF:
Show: ${show}
Topic: ${d.topic}
Niche: ${d.niche || 'general'}
Format: ${d.format || 'solo'}
Audience: ${d.audience || 'general listeners'}
Tone: ${d.tone || 'conversational'}
Desired end emotion: ${d.emotion || 'inspired'}
Anchor/personal story: ${d.anchor || 'none provided'}
${d.guest ? 'Guest: ' + d.guest : ''}
${d.guestInstagram ? 'Guest Instagram: ' + d.guestInstagram : ''}
${d.questionStyle ? 'Interview style preference: ' + d.questionStyle : ''}
${d.desired_feeling ? 'Listener should feel: ' + d.desired_feeling : ''}

${d.live_context || ''}

${trends(d.niche)}

${HOOK_PSYCHOLOGY}

${TONE_HANDLING}

${VIRALITY_RUBRIC}

EPISODE TITLE RULES:
- Max 70 characters (count every character)
- First 40-50 chars must contain highest-value keywords
- Generate 3 options: keyword-optimized, emotional/curiosity, contrarian
- Score each for CTR (1.0-10.0)

YOUTUBE DESCRIPTION:
- First 200 characters MUST be keyword-dense — no greetings, no fluff
- 6-8 natural keyword phrases throughout
- End with: ${social ? 'INSERT SOCIAL LINKS' : '[Podcast links here]'}

PODCAST TITLES: Spotify/Apple — conversational, discovery-focused, 3 options

RUN OF SHOW — Use SEGMENTS not acts. Name each segment specifically for this episode.

FOR SOLO EPISODES, these are the professional segments top podcasters use:
1. Cold Open / Hook (0:00-1:30) — Start mid-story or with the most provocative statement
2. Credibility Bridge (1:30-2:30) — Who you are, why you're qualified to speak on this today
3. Topic Setup / Stakes (2:30-5:00) — Frame the problem, why it matters RIGHT NOW
4. Segment 1: [Name it] (5:00-15:00) — First major idea with supporting points
5. Segment 2: [Name it] (15:00-25:00) — Deeper layer, complication, or counterpoint
6. Segment 3: [Name it] (25:00-33:00) — Resolution, practical application
7. Callback + Takeaway (33:00-36:00) — Return to cold open, single sentence takeaway
8. CTA + Close (36:00-38:00) — One clear action for the listener

FOR INTERVIEW EPISODES, the professional segments are:
1. Cold Open — Tease the single best 30-second moment from the interview
2. Guest Intro — Sell the guest in 90 seconds (not just credentials — why THIS person matters to THIS audience)
3. Origin / Backstory — The question that gets the guest talking about why they do what they do
4. Segment 1: The Foundation — Establish the core idea or expertise
5. Segment 2: The Real Story — Go deeper, find the tension, get personal
6. Segment 3: The Controversy / Insight — Push for the take they don't usually say out loud
7. Segment 4: Practical / What Now — Actionable takeaway for the listener
8. Lightning Round — 3-5 rapid questions (reveal character, create short-form moments)
9. Where to Find Guest + Close

INTERVIEW GUIDE — PROFESSIONAL FORMAT:
This is a production document, not a list of questions. Model this after how Joe Rogan, Diary of a CEO, Andrew Huberman, and Lex Fridman prep their interviews.

For INTERVIEW format, generate a full professional interview guide with:
- Each segment has an OBJECTIVE (what you want the audience to feel/learn)
- Opening question for each segment (hook-style, not generic)
- 2-3 follow-up probes (the questions that go deeper when they answer)
- 🔥 CLIP MOMENT flags — questions specifically designed to produce a shareable 60-second moment
- Clip potential score (1-10) for flagged questions
- Intent label: [Emotional] [Contrarian] [Story] [Tension] [Practical] [Revelation]

For SOLO format, the interview guide becomes a TALKING POINTS GUIDE:
- Each segment has key points to hit
- Transition lines between segments
- Moments to pause and let the point land
- Personal story prompts (what moment from your life connects here?)

🔥 CLIP MOMENT criteria: Would this answer, if 60 seconds long, stop someone mid-scroll? Does it challenge a belief, reveal a surprising truth, or create an emotional moment?

Return ONLY this JSON — no markdown, no preamble:
{
  "hooks": [
    {
      "text": "exact words spoken as first sentence of episode",
      "virality_score": 8.6,
      "format_tag": "🔥 Contrarian",
      "viral_comparison": "Hooks using this format averaged 2.1M YouTube views in this niche",
      "why_it_works": "specific mechanism for THIS topic and audience",
      "delivery_note": "one specific delivery instruction"
    }
  ],
  "youtube_titles": [
    {
      "text": "Title under 70 chars",
      "char_count": 52,
      "ctr_score": 9.1,
      "type": "Contrarian",
      "why": "why this gets clicks in this specific niche",
      "keywords": ["keyword1","keyword2","keyword3"]
    }
  ],
  "podcast_titles": [
    {
      "text": "Podcast platform title",
      "ctr_score": 8.4,
      "type": "Curiosity-gap",
      "why": "why this works for Spotify/Apple discovery"
    }
  ],
  "episode_blueprint": {
    "objective": "One sentence — what this episode achieves for the listener",
    "big_promise": "Why someone clicks — what they believe they will get",
    "target_listener": "Specific person, specific situation they are in right now",
    "emotional_arc": ["Curiosity", "Tension", "Insight", "Resolution"],
    "key_takeaways": ["takeaway 1", "takeaway 2", "takeaway 3"],
    "listener_outcome": "What they do or feel differently after listening"
  },
  "run_of_show": [
    {
      "segment": "Cold Open",
      "duration": "0:00-1:30",
      "objective": "Hook them before they can leave",
      "talking_points": ["specific opening line or story beat", "the provocative statement"],
      "transition": "how you move to the next segment",
      "notes": "specific direction for THIS episode"
    }
  ],
  "interview_guide": ${isInterview ? `[
    {
      "segment": "Cold Open Tease",
      "objective": "Make them want to stay for the full conversation",
      "opening_question": "hook-style question that opens the segment",
      "follow_ups": ["follow-up probe 1", "follow-up probe 2"],
      "clip_moment": false,
      "clip_score": null,
      "intent": "Setup"
    },
    {
      "segment": "Origin Story",
      "objective": "Build connection — why does this guest care so deeply about this?",
      "opening_question": "What was the moment you realized this was going to define your life?",
      "follow_ups": ["What did you almost do instead?", "When did it stop feeling like a choice?"],
      "clip_moment": true,
      "clip_score": 8,
      "intent": "Story"
    },
    {
      "segment": "The Foundation",
      "objective": "Establish the core expertise — what does this person know that others miss?",
      "opening_question": "hook-phrased question specific to guest expertise",
      "follow_ups": ["follow-up 1", "follow-up 2"],
      "clip_moment": false,
      "clip_score": null,
      "intent": "Educational"
    },
    {
      "segment": "The Real Story",
      "objective": "Go deeper — find the tension, failure, or turning point",
      "opening_question": "question designed to surface a personal story or failure",
      "follow_ups": ["What did you learn that you couldn't have learned any other way?", "What would you tell someone standing exactly where you were?"],
      "clip_moment": true,
      "clip_score": 9,
      "intent": "Tension + Story"
    },
    {
      "segment": "The Controversy",
      "objective": "Get the take they don't usually say — the one that creates clips",
      "opening_question": "What's the most controversial thing you actually believe about [topic] that most people in your field would push back on?",
      "follow_ups": ["Where does that belief come from?", "Who gets most upset when you say that?"],
      "clip_moment": true,
      "clip_score": 10,
      "intent": "Contrarian"
    },
    {
      "segment": "Practical Takeaway",
      "objective": "Give the listener something they can do today",
      "opening_question": "If someone listening right now is dealing with [specific situation], what is the one thing you would tell them to do first?",
      "follow_ups": ["What mistake do people make when they try to do that?", "How do they know when it's working?"],
      "clip_moment": false,
      "clip_score": null,
      "intent": "Practical"
    },
    {
      "segment": "Lightning Round",
      "objective": "Reveal character, create short-form moments, end on energy",
      "opening_question": "I want to end with a few quick ones — first thing that comes to mind.",
      "follow_ups": ["Best piece of advice you ever ignored?", "What do you know now that you wish you'd known at the start?", "What are you most afraid people misunderstand about you?"],
      "clip_moment": true,
      "clip_score": 7,
      "intent": "Character"
    }
  ]` : `[
    {
      "segment": "Core Talking Points",
      "objective": "Map the key ideas across the episode",
      "opening_question": null,
      "follow_ups": [],
      "talking_points": ["main idea 1 with supporting detail", "main idea 2 with supporting detail", "main idea 3 with supporting detail"],
      "clip_moment": false,
      "clip_score": null,
      "intent": "Educational",
      "personal_prompt": "What moment from your own life connects to this idea?"
    },
    {
      "segment": "Personal Story Beat",
      "objective": "Make this episode yours — not information, experience",
      "opening_question": null,
      "follow_ups": [],
      "talking_points": ["the specific story or moment to share", "what you felt in that moment", "what changed after"],
      "clip_moment": true,
      "clip_score": 8,
      "intent": "Story",
      "personal_prompt": "Be specific — a place, a time, a person. Vague stories don't connect."
    },
    {
      "segment": "The Contrarian Take",
      "objective": "Say the thing most people in your niche won't say",
      "opening_question": null,
      "follow_ups": [],
      "talking_points": ["the unpopular opinion", "why you believe it despite the pushback", "the evidence or experience that changed your mind"],
      "clip_moment": true,
      "clip_score": 9,
      "intent": "Contrarian",
      "personal_prompt": "What would make other podcasters in your niche uncomfortable to hear you say?"
    },
    {
      "segment": "Practical Close",
      "objective": "Leave the listener with something actionable",
      "opening_question": null,
      "follow_ups": [],
      "talking_points": ["the single most important action", "what gets in the way of doing it", "how they know it's working"],
      "clip_moment": false,
      "clip_score": null,
      "intent": "Practical",
      "personal_prompt": "What is the one thing you wish someone had told you to do first?"
    }
  ]`},
  "show_notes": "2-3 paragraphs. SEO-aware. Human tone. What the episode covers and who it is for.",
  "youtube_description": "First 200 chars keyword-dense. Then 2-3 paragraphs. Then timestamps. Then social links.${social}",
  "spotify_description": "120-160 words. Conversational. What it covers, who should listen, key takeaway.",
  "distribution": {
    "instagram": "conversational caption, ends with question, 2-3 hashtags max",
    "tiktok": "short punchy caption, 3-4 hashtags",
    "youtube_shorts": "keyword-first caption, hook format, 2-3 hashtags",
    "spotify_social": "shareable post about the episode",
    "email": {
      "subject": "subject line with open-loop",
      "preview": "preview text (90 chars)",
      "body": "2-3 paragraph newsletter email about the episode"
    }
  },
  "episode_score": {
    "overall": 8.4,
    "hook_strength": 8.8,
    "topic_relevance": 8.2,
    "viral_potential": 8.0,
    "coach_note": "One sentence on biggest strength + one specific thing to push it further."
  },
  "trending_insight": "2 sentences: what is performing in this niche right now and why this episode fits that pattern.",
  "top_hook": 0,
  "top_youtube_title": 0,
  "top_podcast_title": 0${isInterview && d.guest ? `,
  "guest_brief": {
    "guest_name": "${d.guest}",
    "guest_handle": "${d.guestInstagram || ''}",
    "one_liner": "one sentence — who they are and why this audience should care",
    "expertise_areas": ["area 1", "area 2", "area 3"],
    "suggested_host_intro": "90-second host intro script written for this specific audience — sell the guest, not just their title",
    "pre_show_research": ["specific thing to look up or listen to before the call", "a specific piece of their content to reference in the interview"],
    "content_style": "how this guest typically shows up on podcasts — their communication style, energy, tendencies",
    "conversation_risks": ["topic or area to handle carefully or avoid"],
    "best_personal_question": "the one question most hosts never ask that would reveal the most interesting side of this guest",
    "host_prep_notes": "2-3 sentences of production notes — what angle to push, what energy to match, what the listener most needs from this conversation"
  }` : ''
}

CRITICAL — keep ALL text values SHORT and specific. Max 2 sentences for any description field. No filler. No padding. Specificity beats length every time.
Generate: 4 hooks · 3 YouTube titles · 3 podcast titles · episode blueprint · segmented run of show · interview guide · show notes · descriptions · distribution copy.
Everything specific to this topic. Honest scores.`

  // Use streaming + sonnet with high token limit — episode JSON can be 5-8k tokens
  const res = await callAPIStream(system, `Generate complete episode package for: "${d.topic}". Return only valid JSON, no markdown, no preamble, no trailing text.`, 8000, 'sonnet')
  return parseJSON(res)
}

// ── HookLab ───────────────────────────────────────────────────────────────────
export async function generateHooks(d) {

  const system = `You are HookLab — specialist in podcast hooks and viral short-form overlays.
Generate hooks and text overlays that are specific to this creator's topic and audience.

BRIEF: Topic: ${d.topic} | Niche: ${d.niche} | Format: ${d.format||'solo'} | Audience: ${d.audience} | End emotion: ${d.emotion}

${trends(d.niche)}
${VIRALITY_RUBRIC}

TEXT OVERLAY RULE: Grade 5 reading level MAX. Sounds like a real person — not a marketer.

Return ONLY JSON:
{
  "hooks": [{"text":"","virality_score":8.4,"virality_breakdown":{"pattern_match":2.2,"keyword_gravity":2.0,"scroll_stop":2.2,"specificity":2.0},"format_tag":"","viral_comparison":"","why_it_works":"","delivery_note":""}],
  "text_overlays": [{"text":"","virality_score":8.7,"placement":"","why":""}],
  "trending_insight": "",
  "top_hook": 0
}
Generate 5 hooks, 5 text overlays. Honest scores. Specific to this topic.`

  const res = await callAPI(system, `Generate hooks and overlays for "${d.topic}". Return only JSON.`, 2500)
  return parseJSON(res)
}

// ── Short-Form Builder ────────────────────────────────────────────────────────
export async function generateShortForm(d) {

  const system = `You are Epio's short-form specialist. Generate text overlays and captions only.
BRIEF: Topic: ${d.topic} | Niche: ${d.niche} | Platform: ${d.platform||'all'} | Style: ${d.style||'punchy'}
${trends(d.niche)}
TEXT OVERLAY: Grade 5 level MAX. Human. Short. Punchy. Stops scroll on mute.
Return ONLY JSON:
{
  "text_overlays": [{"text":"","timing":"","purpose":"","virality_score":8.8}],
  "captions": {"tiktok":"","instagram":"","youtube_shorts":""},
  "virality_score": 8.2,
  "viral_comparison": "",
  "tip": ""
}
Generate 6 overlays + 3 captions. Specific to this topic.`

  const res = await callAPI(system, `Generate overlays and captions for "${d.topic}". Return only JSON.`, 1800)
  return parseJSON(res)
}

// ── Podcast Naming ────────────────────────────────────────────────────────────
export async function generateNames(d) {

  const system = `You are a podcast brand naming expert. Generate 5 memorable podcast name options.
Each name: 2–4 words, easy to say, easy to search, distinct enough to own.
BRIEF: About: ${d.about} | Audience: ${d.audience} | Vibe: ${d.vibe} | Niche: ${d.niche}
Return ONLY JSON:
{"names":[{"name":"Show Name","why":"one sentence why this works","tagline":"short show tagline","vibe":"Modern/Bold/Warm/Playful/Premium"}]}
Generate 5 names. Vary the styles. No generic podcast name clichés.`

  const res = await callAPI(system, `Generate podcast names. Return only JSON.`, 900)
  return parseJSON(res)
}

// ── Full AI-driven interview conductor ───────────────────────────────────────
export async function conductInterview(conversationHistory, collectedData) {

  const fmt = (collectedData.format || '').toLowerCase()
  const needs = {
    show_name:   !collectedData.show_name && !collectedData.show_name_skipped,
    format:      !collectedData.format,
    host_count:  !collectedData.host_count,
    topic:       !collectedData.topic,
    niche:       !collectedData.niche,
    emotion:     !collectedData.emotion,
    anchor:      !collectedData.anchor && !collectedData.anchor_skipped,
    guest:       !collectedData.guest && (fmt.includes('guest') || fmt.includes('panel')),
  }

  const stillNeeds = Object.keys(needs).filter(k => needs[k])

  // If refining after results generated — just respond conversationally
  if (collectedData.refining) {
    const refineSystem = [
      'You are Epio — the creator just saw their episode results and wants to refine something.',
      'Have a natural conversation. Answer their question, adjust their request, suggest improvements.',
      'Be direct and helpful. Sound like a producer reviewing work with their client.',
      'If they ask you to change something specific (a hook, an angle, the tone), acknowledge it and tell them to regenerate or what to change.',
      '2-3 sentences max.',
    ].join('\n')
    const res = await callAPI(refineSystem, conversationHistory, 200, true)
    return { text: res || 'What would you like to adjust?', done: false, chips: null }
  }

  if (stillNeeds.length === 0) {
    return { text: 'Perfect. Building your episode package now…', done: true, chips: null }
  }

  const known = [
    collectedData.show_name  ? 'Show name: '   + collectedData.show_name  : '',
    collectedData.host_name  ? 'Host name: '   + collectedData.host_name  : '',
    collectedData.host_count ? 'Hosts: '       + collectedData.host_count : '',
    collectedData.format     ? 'Format: '      + collectedData.format     : '',
    collectedData.topic      ? 'Topic: '       + collectedData.topic      : '',
    collectedData.niche      ? 'Niche: '       + collectedData.niche      : '',
    collectedData.emotion    ? 'Emotion: '     + collectedData.emotion    : '',
    collectedData.anchor     ? 'Anchor: '      + collectedData.anchor     : '',
    collectedData.guest      ? 'Guest: '       + collectedData.guest      : '',
  ].filter(Boolean).join('\n')

  const systemLines = [
    'You are Epio — a sharp, warm, funny AI podcast producer with real personality.',
    'You sound like the best producer in the game: direct, encouraging, specific, and human.',
    '',
    'ACTIVE LISTENING — CRITICAL:',
    'You remember everything the creator tells you and use it naturally.',
    `- If they said their show is called "${collectedData.show_name}", say their show name back when relevant.`,
    `- If they said their name is "${collectedData.host_name}", use it naturally in conversation.`,
    `- If they said format is "${collectedData.format}", tailor all questions to that format.`,
    '- Reference what they said earlier. Make them feel heard. This is the most important thing.',
    '- Example: "Given what you said about [topic], that hooks really well into..."',
    '',
    'PERSONALITY:',
    '- Warm, direct, never robotic. Sound like a real person.',
    '- Get excited about genuinely great episode ideas.',
    '- Gentle humor when appropriate. Never preachy.',
    '- Off-topic? One funny redirect, then back to the episode.',
    '  Examples: "Love the energy, but I\'ve got hooks to write." / "That\'s outside my studio — back to the episode."',
    '',
    'EXACT CONVERSATION FLOW — follow this order:',
    '',
    '1. SHOW NAME (if not collected):',
    '   Ask: "First — what\'s your podcast called? If you don\'t have a name yet, I can help with that."',
    '   If they have no name, offer to help: ask what it\'s about, suggest 3 names, let them pick.',
    '',
    '2. FORMAT CHIPS (immediately after show name):',
    '   Say something like: "Love it. What format is [show name]?"',
    '   Then show: [CHIPS: Solo monologue | Guest interview | Co-hosted | Narrative / documentary | Panel show | Hybrid]',
    '',
    '3. HOST COUNT CHIPS (immediately after format):',
    '   Say: "Got it. How many hosts on [show name]?"',
    '   Then show: [CHIPS: Just me | Two co-hosts | Three+ co-hosts]',
    '',
    '4. EPISODE TOPIC:',
    '   Now ask about the specific episode. Keep it tight — one question.',
    '   If they give a broad topic, share 1 sentence on what\'s performing in that space, then ask to narrow.',
    '',
    '5. NUMBERED OPTIONS — when giving ideas/angles:',
    '   Always number them 1-5. Mark one as (trending). User can reply with a number.',
    '   Each item on its own line. Never inline bullet separators.',
    '',
    '6. COLLECT: hook angle, audience, outcome, tension/surprise, story beat.',
    '   One question at a time. Skip what you can infer.',
    '',
    '7. DONE: say exactly "Perfect. Building your episode now."',
    '',
    'WHAT YOU KNOW SO FAR:',
    known || '(start of conversation)',
    '',
    'STILL NEED: ' + stillNeeds.join(', '),
    'NEXT PRIORITY: ' + stillNeeds[0],
    '',
    'NEVER use Q4 dates or specific years in trend data. Say "right now" or "currently".',
    'NEVER ask two questions at once.',
    'NEVER say "Great!", "Excellent!", "Absolutely!" — sounds like a bot.',
  ]

    const system = systemLines.join('\n')
  // API requires at least one message — seed with a start prompt if history is empty
  const history = (conversationHistory && conversationHistory.length > 0)
    ? conversationHistory
    : [{ role: 'user', content: 'Start the interview.' }]

  const res = await callAPI(system, history, 260, true)

  const chipMatch = res ? res.match(/\[CHIPS:([^\]]+)\]/) : null
  let text = res || ''
  let chips = null

  if (chipMatch) {
    text = res.replace(chipMatch[0], '').trim()
    chips = chipMatch[1].split('|').map(function(c) { return c.trim() }).filter(Boolean).slice(0, 5)
  }

  return { text: text, done: false, chips: chips }
}

// ── nextQuestion — used by HookLab ────────────────────────────────────────────
export async function nextQuestion(messages, step, tool) {
  const sys = 'You are Epio — warm, direct, like a great producer. ' +
    'Ask ONE follow-up question. Step: ' + step + '. ' +
    'Be brief (1-2 sentences). Sound human. ' +
    'If step is done say only: Perfect. Building your results now.'
  return await callAPI(sys, messages, 180, true)
}

// ── Helpers ───────────────────────────────────────────────────────────────────
// Streaming call — used for large structured generation to avoid Vercel timeouts.
// Returns the full accumulated text once the stream completes.
async function callAPIStream(system, userMsg, maxTokens = 4000, modelHint = 'haiku') {
  const messages = [{ role: 'user', content: userMsg }]

  let res
  try {
    res = await fetch('/api/claude', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ system, messages, max_tokens: maxTokens, model: modelHint, stream: true, user_id: _currentUserId }),
    })
  } catch (networkErr) {
    throw new Error('Network error — check your internet connection and try again.')
  }

  if (!res.ok) {
    const e = await res.json().catch(() => ({}))
    const msg = e.error || ''
    if (res.status === 401 || res.status === 403) throw new Error('Invalid API key — check ANTHROPIC_API_KEY in Vercel settings')
    if (res.status === 429) throw new Error('Rate limit hit — wait 30 seconds and try again.')
    if (res.status === 500 && msg.includes('ANTHROPIC_API_KEY')) throw new Error('ANTHROPIC_API_KEY is missing from Vercel environment variables.')
    throw new Error(msg || `Server error (${res.status}) — please try again.`)
  }

  // Read SSE stream
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let fullText = ''
  let buf = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buf += decoder.decode(value, { stream: true })
    const lines = buf.split('\n')
    buf = lines.pop()

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue
      const payload = line.slice(6).trim()
      try {
        const evt = JSON.parse(payload)
        if (evt.done && evt.text) { fullText = evt.text; break }
        if (evt.delta) fullText += evt.delta
        if (evt.error) {
          if (evt.error === 'generation_timeout') {
            throw new Error('Generation timed out. Please try again — if this keeps happening, check your Vercel function logs.')
          }
          throw new Error(evt.error)
        }
      } catch (parseErr) {
        if (parseErr.message && !parseErr.message.includes('JSON')) throw parseErr
        // skip malformed SSE lines
      }
    }
  }

  return fullText
}

// Non-streaming call — for short conversational replies
async function callAPI(system, userMsg, maxTokens = 2000, isConversation = false) {
  const messages = isConversation
    ? (Array.isArray(userMsg) ? userMsg : [{ role: 'user', content: userMsg }])
    : [{ role: 'user', content: userMsg }]

  let res
  try {
    res = await fetch('/api/claude', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ system, messages, max_tokens: maxTokens, model: 'sonnet', user_id: _currentUserId }),
    })
  } catch (networkErr) {
    throw new Error('Network error — check your internet connection and try again.')
  }

  if (!res.ok) {
    let errorMsg
    try {
      const e = await res.json()
      errorMsg = e.error || ''
    } catch {
      errorMsg = ''
    }

    if (res.status === 504 || res.status === 408) {
      throw new Error('Generation timed out — please try again. If this keeps happening, verify your ANTHROPIC_API_KEY is set in Vercel.')
    }
    if (res.status === 401 || res.status === 403) {
      throw new Error('Invalid API key. Go to Vercel → Settings → Environment Variables and make sure ANTHROPIC_API_KEY is set correctly.')
    }
    if (res.status === 429) {
      throw new Error('Rate limit hit — wait 30 seconds and try again.')
    }
    if (res.status === 500 && errorMsg.includes('ANTHROPIC_API_KEY')) {
      throw new Error('ANTHROPIC_API_KEY is missing from Vercel. Go to Vercel → Settings → Environment Variables → Add ANTHROPIC_API_KEY.')
    }

    throw new Error(errorMsg || `Server error (${res.status}) — please try again.`)
  }

  const data = await res.json()
  return data.text || ''
}

function parseJSON(raw) {
  if (!raw) throw new Error('Empty response from AI. Please try again.')

  // Strip markdown code fences and any leading/trailing prose
  const clean = raw
    .replace(/```json\s*/gi, '')
    .replace(/```\s*/g, '')
    .trim()

  // Find outermost JSON object
  const start = clean.indexOf('{')
  if (start === -1) throw new Error('No JSON found in response. Please try again.')

  // Try progressively: full text → last-brace slice → auto-closed
  const candidates = []

  // Candidate 1: full text from first { to last }
  const lastBrace = clean.lastIndexOf('}')
  if (lastBrace > start) candidates.push(clean.slice(start, lastBrace + 1))

  // Candidate 2: auto-close unclosed brackets (handles truncation)
  candidates.push(autoCloseJSON(clean.slice(start)))

  for (const candidate of candidates) {
    // Fix 1: direct parse
    try { return JSON.parse(candidate) } catch { /* keep trying */ }

    // Fix 2: remove trailing commas, convert single-quote values
    try {
      const f = candidate
        .replace(/,\s*([}\]])/g, '$1')        // trailing commas
        .replace(/:\s*'([^']*)'/g, ': "$1"')  // single → double quotes
        .replace(/\n/g, '\\n')                // bare newlines inside strings
      return JSON.parse(f)
    } catch { /* keep trying */ }

    // Fix 3: aggressive — also strip control chars
    try {
      // eslint-disable-next-line no-control-regex
      const f = candidate.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
        .replace(/,\s*([}\]])/g, '$1')
      return JSON.parse(f)
    } catch { /* keep trying */ }
  }

  console.error('JSON parse failed. Raw preview:', raw.slice(0, 800))
  throw new Error('The AI response could not be parsed. Please try again.')
}

// Auto-close truncated JSON by counting unclosed brackets/braces
function autoCloseJSON(str) {
  const stack = []
  let inString = false
  let s = str.trim()

  for (let i = 0; i < s.length; i++) {
    const c = s[i]
    if (c === '\\' && inString) { i++; continue }  // skip escaped char
    if (c === '"') { inString = !inString; continue }
    if (inString) continue
    if (c === '{' || c === '[') stack.push(c)
    else if ((c === '}' || c === ']') && stack.length) stack.pop()
  }

  // If truncated mid-string, close the string
  if (inString) s += '"'

  // Remove trailing comma or dangling colon
  s = s.replace(/,\s*$/, '').replace(/:\s*$/, ': null')

  // Close all unclosed structures (innermost first)
  for (let i = stack.length - 1; i >= 0; i--) {
    s += stack[i] === '{' ? '}' : ']'
  }

  return s
}
